import {
  Badge,
  Box,
  Card,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  theme,
} from "@aksara-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import styled from "styled-components";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import { getAllRaffleCities, getAllRaffleMerchants } from "redux/actions";
import { InputSearchbox } from "components/Form/InputSearchbox";
import { toCapitilizeCase } from "utilities/helper/toCapitalizeCase";
import {
  Pagination,
  PaginationDetail,
  PaginationFilter,
} from "components/Pagination";
import FilterKit from "components/FilterKit";

const HeaderWrapper = styled(Box)`
  justify-content: space-between;
  margin-bottom: 30px;
  gap: 20px;
`;

const RaffleMerchantsContent: React.FC = () => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 576 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  const { isLoading } = useSelector((state: any) => state.globalReducer);
  const { raffleMerchants } = useSelector(
    (state: any) => state.raffleMerchantReducer
  );
  const { currentPage, totalPage, limitPage, lengthPage, filterCityPage } =
    useSelector((state: any) => state.raffleMerchantReducer);
  const { citiesFilter } = useSelector((state: any) => state.raffleReducer);

  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
    dispatch(
      getAllRaffleMerchants(
        1,
        event.target.value,
        limitPage,
        filterCityPage
      ) as any
    );
  };

  const handleClearSearchChange = () => {
    setSearchValue("");
    dispatch(
      getAllRaffleMerchants(currentPage, "", limitPage, filterCityPage) as any
    );
  };

  useEffect(() => {
    dispatch(getAllRaffleCities() as any);
    dispatch(getAllRaffleMerchants() as any);
  }, [dispatch]);

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      <HeaderWrapper
        display={"flex"}
        flexDirection={"column"}
        style={{ gap: 20 }}
      >
        <Box display={"flex"}>
          <Text scale={[500, 600]} fontWeight={700}>
            Merchant List
          </Text>
        </Box>

        <Box
          display={"flex"}
          flexDirection={isMobile ? "column" : "row"}
          justifyContent={"space-between"}
          style={{ gap: isMobile ? 10 : 20 }}
        >
          <Box
            display={"flex"}
            flexDirection={isTablet ? "column" : "row"}
            justifyContent={"left"}
            style={{ gap: 5 }}
          >
            <InputSearchbox
              autoComplete="off"
              name="search"
              placeholder="Search..."
              size={32}
              onChange={handleSearchChange}
              onClearButton={handleClearSearchChange}
            />

            <FilterKit
              orderByOptions={[
                {
                  key: citiesFilter,
                  label: "City",
                  options: citiesFilter.map((city: any) => {
                    return {
                      label: city.city,
                      value: city.id ?? "",
                    };
                  }),
                },
              ]}
              onChangeOrder={(e: any) => {
                dispatch(
                  getAllRaffleMerchants(
                    1,
                    "",
                    limitPage,
                    e[citiesFilter] ? e[citiesFilter].value : ""
                  ) as any
                );
              }}
            />
          </Box>
        </Box>
      </HeaderWrapper>

      <Card maxHeight={"100%"} overflow={"auto"} elevation={2}>
        <Table>
          <TableHead
            position={"sticky"}
            top={0}
            backgroundColor={theme.colors.grey02}
          >
            <TableHeadRow>
              <TableHeadCell maxWidth={3}></TableHeadCell>
              <TableHeadCell>Name</TableHeadCell>
              <TableHeadCell>City</TableHeadCell>
              <TableHeadCell>Is Java</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodyRowsSkeleton rowsNumber={10} columnsNumber={4} />
            ) : !raffleMerchants.length ? (
              <TableBodyRow>
                <TableBodyCell colSpan={4} textAlign={"center"}>
                  No data available in table
                </TableBodyCell>
              </TableBodyRow>
            ) : (
              raffleMerchants.map((merchant: any, index: any) => {
                return (
                  <TableBodyRow key={index}>
                    <TableBodyCell>
                      <Badge
                        size="md"
                        variant="success"
                        style={{
                          borderRadius: "100%",
                          padding: 10,
                        }}
                      >
                        {10 * (currentPage - 1) + (index + 1)}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>
                      {toCapitilizeCase(merchant.merchant_name)}
                    </TableBodyCell>
                    <TableBodyCell>
                      {toCapitilizeCase(merchant.city)}
                    </TableBodyCell>
                    <TableBodyCell>
                      <Badge
                        size="md"
                        variant={
                          merchant.is_java !== 1 ? "critical" : "success"
                        }
                      >
                        {merchant.is_java !== 1 ? "No" : "Yes"}
                      </Badge>
                    </TableBodyCell>
                  </TableBodyRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        paddingY={10}
      >
        {!isMobile ? (
          <PaginationDetail
            page={currentPage}
            limit={limitPage}
            length={lengthPage}
          />
        ) : (
          ""
        )}
        <Pagination
          current={currentPage}
          total={totalPage}
          onSelect={(select: any) =>
            dispatch(
              getAllRaffleMerchants(
                select,
                searchValue,
                limitPage,
                filterCityPage
              ) as any
            )
          }
        />
        <PaginationFilter
          selectedItem={limit}
          onChange={(selectedItem) => {
            if (selectedItem) {
              setLimit(selectedItem);
              dispatch(
                getAllRaffleMerchants(
                  1,
                  searchValue,
                  selectedItem,
                  filterCityPage
                ) as any
              );
            }
          }}
          items={[10, 20, 30, 40, 50]}
        />
      </Box>
    </Box>
  );
};

export default RaffleMerchantsContent;
